import React, { useEffect, useState } from "react";
import { Flame, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AddressLink } from "@/components/ContactLinks";
import { resolveImgSrc } from "@/components/ImageUpload";

/**
 * Public banner that lists today's Sponsor-boosted Streak Multiplier venues.
 * Renders nothing when there are no active boosts.
 */
export default function StreakBoostBanner({ compact = false }) {
  const [boosts, setBoosts] = useState(null);
  useEffect(() => {
    api.get("/streak-boosts/active").then(r => setBoosts(r.data.boosts || [])).catch(() => setBoosts([]));
  }, []);

  if (!boosts || boosts.length === 0) return null;

  if (compact) {
    return (
      <Link to="/directory" className="block border border-orange-500/40 bg-gradient-to-r from-orange-500/15 to-orange-500/5 rounded p-3 hover:from-orange-500/25 transition"
        data-testid="boost-banner-compact">
        <div className="flex items-center gap-2 text-sm">
          <Flame size={16} className="text-orange-400"/>
          <span className="font-bold text-orange-300">2X STREAK DAY</span>
          <span className="text-white/60">at {boosts.length} sponsored venue{boosts.length !== 1 ? "s" : ""} — tap to see</span>
        </div>
      </Link>
    );
  }

  return (
    <div className="rounded border border-orange-500/40 bg-gradient-to-r from-orange-500/10 to-transparent p-4"
      data-testid="boost-banner-full">
      <div className="flex items-center gap-2">
        <Flame size={18} className="text-orange-400"/>
        <div className="text-xs uppercase tracking-widest text-orange-300 font-bold">Today · 2X Streak Multiplier</div>
      </div>
      <div className="text-sm text-white/70 mt-1">
        Check in at any of these Sponsor-boosted venues today and it counts as <b className="text-orange-300">2 streak days</b>. Perfect for keeping your streak alive or unlocking the next bonus entry sooner.
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {boosts.map(b => (
          <Link key={b.id} to={`/business/${b.business_id}`}
            className="flex items-center gap-3 bg-[#0b1428] border border-white/10 hover:border-orange-500/60 rounded p-2 transition"
            data-testid={`boost-venue-${b.business_id}`}>
            {b.logo ? (
              <img src={resolveImgSrc(b.logo)} alt="" className="w-10 h-10 rounded object-cover"/>
            ) : (
              <div className="w-10 h-10 rounded bg-orange-500/20 flex items-center justify-center"><Flame size={16} className="text-orange-400"/></div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate flex items-center gap-1">
                {b.business_name}
                <span className="text-[10px] px-1 py-0.5 bg-orange-500/25 text-orange-300 rounded font-bold">{b.multiplier}X</span>
              </div>
              <div className="text-[11px] text-white/50 flex items-center gap-1 truncate" onClick={(e)=>e.stopPropagation()}>
                <MapPin size={9}/>
                <AddressLink address={b.address} className="hover:text-primary transition-colors truncate">{b.address}</AddressLink>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {boosts[0]?.sponsor_note && (
        <div className="mt-3 text-[10px] text-white/40 italic">{boosts[0].sponsor_note}</div>
      )}
    </div>
  );
}
